// Minimal inline icon set for the owner nav — 24x24, currentColor stroke.
// Kept tiny and local; no icon-library dependency.

type IconProps = { className?: string }

const base = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
}

export function DashboardIcon(p: IconProps) {
  return (
    <svg {...base} className={p.className} aria-hidden="true">
      <rect x="3" y="3" width="7" height="9" rx="1.5" />
      <rect x="14" y="3" width="7" height="5" rx="1.5" />
      <rect x="14" y="12" width="7" height="9" rx="1.5" />
      <rect x="3" y="16" width="7" height="5" rx="1.5" />
    </svg>
  )
}

export function BrandingIcon(p: IconProps) {
  return (
    <svg {...base} className={p.className} aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="3.2" />
    </svg>
  )
}

export function PosterIcon(p: IconProps) {
  return (
    <svg {...base} className={p.className} aria-hidden="true">
      <rect x="5" y="3" width="14" height="18" rx="1.5" />
      <line x1="8" y1="6.5" x2="16" y2="6.5" />
      <rect x="9" y="11" width="6" height="6" rx="0.5" />
    </svg>
  )
}

export function BranchIcon(p: IconProps) {
  return (
    <svg {...base} className={p.className} aria-hidden="true">
      <path d="M3 21V9l9-6 9 6v12" />
      <path d="M9 21v-6h6v6" />
    </svg>
  )
}

export function StaffIcon(p: IconProps) {
  return (
    <svg {...base} className={p.className} aria-hidden="true">
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3 20c0-3.3 2.7-5 6-5s6 1.7 6 5" />
      <path d="M16 11.5a3 3 0 1 0-1-5.8" />
      <path d="M18 20c0-2.5-1-4.2-3-4.8" />
    </svg>
  )
}

export function CustomersIcon(p: IconProps) {
  return (
    <svg {...base} className={p.className} aria-hidden="true">
      <circle cx="12" cy="7" r="3.5" />
      <path d="M5 21c0-3.9 3.1-6.5 7-6.5s7 2.6 7 6.5" />
    </svg>
  )
}

export function ProgramsIcon(p: IconProps) {
  return (
    <svg {...base} className={p.className} aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 10h18" />
      <circle cx="7.5" cy="14.5" r="1.2" />
      <circle cx="11.5" cy="14.5" r="1.2" />
    </svg>
  )
}

export function BroadcastsIcon(p: IconProps) {
  return (
    <svg {...base} className={p.className} aria-hidden="true">
      <path d="M4 9v6h4l6 4V5L8 9H4z" />
      <path d="M17.5 8.5a5 5 0 0 1 0 7" />
    </svg>
  )
}

export function BillingIcon(p: IconProps) {
  return (
    <svg {...base} className={p.className} aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 10h18M7 15h4" />
    </svg>
  )
}

export function SupportIcon(p: IconProps) {
  // Circle + question mark — standard help/support glyph; reads instantly
  // in the More sheet without competing with the Billing icon next to it.
  return (
    <svg {...base} className={p.className} aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.2a2.5 2.5 0 1 1 3.5 2.3c-0.7 0.4-1 0.9-1 1.5v0.5" />
      <circle cx="12" cy="16.3" r="0.8" fill="currentColor" stroke="none" />
    </svg>
  )
}

export function MenuIcon(p: IconProps) {
  return (
    <svg {...base} className={p.className} aria-hidden="true">
      <path d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  )
}

// Stamp icon — a square outline with a central check-style mark, evoking
// the act of stamping a card. Used by the owner stamping nav item (4a-7).
export function StampIcon(p: IconProps) {
  return (
    <svg {...base} className={p.className} aria-hidden="true">
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <path d="M8 12l3 3 5-6" />
    </svg>
  )
}

// Logs icon — clipboard-with-lines for the owner stamp-history page.
export function LogsIcon(p: IconProps) {
  return (
    <svg {...base} className={p.className} aria-hidden="true">
      <rect x="5" y="4" width="14" height="17" rx="2" />
      <path d="M9 4h6v3H9z" />
      <path d="M9 12h6M9 16h4" />
    </svg>
  )
}

// Referral icon — two linked people for the owner Referrals page.
export function ReferralIcon(p: IconProps) {
  return (
    <svg {...base} className={p.className} aria-hidden="true">
      <circle cx="8" cy="8" r="3" />
      <path d="M3 20c0-2.8 2.2-5 5-5s5 2.2 5 5" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M15.5 14.2A4.5 4.5 0 0 1 21 18.5" />
    </svg>
  )
}

// Analytics icon — rising trend line for the owner Analytics page.
export function AnalyticsIcon(p: IconProps) {
  return (
    <svg {...base} className={p.className} aria-hidden="true">
      <path d="M4 4v15a1 1 0 0 0 1 1h15" />
      <path d="M8 15l3.5-4 3 2.5L19 8" />
    </svg>
  )
}

// Torch icon — the scanner's flashlight toggle (owner + staff scanners).
// Same glyph on and off; `.stamp-torch.is-on` carries the state via color,
// not a second icon — matches the rest of this set's currentColor pattern.
export function TorchIcon(p: IconProps) {
  return (
    <svg {...base} className={p.className} aria-hidden="true">
      <circle cx="12" cy="12" r="4.5" />
      <path d="M12 3v2.5M12 18.5V21M3 12h2.5M18.5 12H21M6.3 6.3l1.8 1.8M15.9 15.9l1.8 1.8M17.7 6.3l-1.8 1.8M8.1 15.9l-1.8 1.8" />
    </svg>
  )
}

// Stamp-sound toggle (owner + staff scanners). Unlike TorchIcon, on/off get
// distinct glyphs — a muted speaker with waves reads ambiguously at 20px, so
// the mute state swaps in an explicit X.
export function SoundOnIcon(p: IconProps) {
  return (
    <svg {...base} className={p.className} aria-hidden="true">
      <path d="M4 9v6h4l5 4V5L8 9H4z" />
      <path d="M16.5 8.5a5 5 0 0 1 0 7" />
      <path d="M19 6a8.5 8.5 0 0 1 0 12" />
    </svg>
  )
}

export function SoundOffIcon(p: IconProps) {
  return (
    <svg {...base} className={p.className} aria-hidden="true">
      <path d="M4 9v6h4l5 4V5L8 9H4z" />
      <path d="M16 9l5 6M21 9l-5 6" />
    </svg>
  )
}

// Secret-menu icon — a padlock with a small star, for the owner Secret Menu
// settings page (off-menu stamp / hidden reward config).
export function SecretMenuIcon(p: IconProps) {
  return (
    <svg {...base} className={p.className} aria-hidden="true">
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V7.5a4 4 0 0 1 8 0V11" />
      <path d="M12 14.3l.9 1.9 2.1.3-1.5 1.5.35 2.1-1.85-1-1.85 1 .35-2.1-1.5-1.5 2.1-.3z" />
    </svg>
  )
}
